// src/components/TradesPanel.jsx
// 매매내역 (audit log + 수동 추가/수정/삭제)
import React, { useEffect, useState } from "react";
import { fetchTrades, addTrade, updateTrade, deleteTrade } from "../hooks/useSheets";

function fmt(n, d = 2) {
  if (n == null || n === "" || isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso); }
}
// timestamp(ISO) → "YYYY-MM-DDTHH:mm" (datetime-local input 값)
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
const SIDE_COLOR = { "매수": "#0a7c59", "매도": "#c0392b", "수정": "#888" };
const SIDE_BG    = { "매수": "#e8f7ef", "매도": "#fdecea", "수정": "#f0f0f0" };

const EMPTY_FORM = {
  _row: null, timestamp: "", action: "manual", side: "매수",
  ticker: "", name: "", market: "US",
  qtyChange: "", qtyAfter: "", price: "", currency: "USD",
  exchangeRate: "", notes: "",
};

export default function TradesPanel() {
  const [trades, setTrades] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // form state when editing/adding; null = view mode
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchTrades();
      if (res.error) throw new Error(res.error);
      setTrades(res.trades || []);
    } catch (e) {
      setError(e.message || "로딩 실패");
      setTrades([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startAdd = () => setEditing({ ...EMPTY_FORM, timestamp: isoToLocalInput(new Date().toISOString()) });
  const startEdit = (t) => setEditing({
    _row: t._row,
    timestamp: isoToLocalInput(t.timestamp),
    action: t.action || "manual",
    side: t.side || "매수",
    ticker: t.ticker || "",
    name: t.name || "",
    market: t.market || "US",
    qtyChange: t.qtyChange ?? "",
    qtyAfter: t.qtyAfter ?? "",
    price: t.price ?? "",
    currency: t.currency || "USD",
    exchangeRate: t.exchangeRate ?? "",
    notes: t.notes || "",
  });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.ticker) { setError("ticker는 필수"); return; }
    setBusy(true); setError(null);
    try {
      const payload = {
        ...editing,
        timestamp: editing.timestamp ? new Date(editing.timestamp).toISOString() : "",
      };
      const res = editing._row ? await updateTrade(payload) : await addTrade(payload);
      if (res.error) throw new Error(res.error);
      setEditing(null);
      await load();
    } catch (e) { setError(e.message || "저장 실패"); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`정말 삭제? (${t.ticker} ${t.side})`)) return;
    setBusy(true); setError(null);
    try {
      const res = await deleteTrade(t._row);
      if (res.error) throw new Error(res.error);
      await load();
    } catch (e) { setError(e.message || "삭제 실패"); }
    finally { setBusy(false); }
  };

  const filtered = (trades || []).filter(t =>
    !filter || (t.ticker || "").toString().toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>매매내역</h3>
          <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
            메인 페이지의 holdings 변경이 자동 기록됨. 수동 추가/수정/삭제 가능.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            placeholder="티커 필터"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #ddd", borderRadius: 6, width: 140 }}
          />
          <button onClick={load} disabled={loading || busy} style={{
            padding: "6px 12px", fontSize: 12, borderRadius: 6, border: "1px solid #ddd",
            background: "#fff", cursor: "pointer", color: "#555",
          }}>{loading ? "..." : "🔄"}</button>
          <button onClick={startAdd} disabled={busy || editing} style={{
            padding: "6px 14px", fontSize: 12, borderRadius: 6, border: "none",
            background: "#1a1a2e", color: "#fff", fontWeight: 700, cursor: editing ? "not-allowed" : "pointer",
            opacity: editing ? 0.4 : 1,
          }}>+ 추가</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 20px", background: "#fff0f0", color: "#c0392b", fontSize: 12 }}>
          ⚠️ {error}
        </div>
      )}

      {editing && <EditForm form={editing} setForm={setEditing} onSave={save} onCancel={cancel} busy={busy} />}

      {trades === null || loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 13 }}>
          {loading ? "불러오는 중..." : "..."}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 13 }}>
          {trades.length === 0
            ? "매매내역 없음. 메인에서 holdings 수정하거나 위 '+ 추가' 버튼으로 직접 입력."
            : "필터에 해당하는 항목 없음"}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
                {["시각", "구분", "티커", "수량변화", "수량(잔고)", "단가", "통화", "환율", "메모", ""].map((h, i) => (
                  <th key={i} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888", textAlign: h === "메모" || h === "티커" || h === "시각" ? "left" : "right", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const sideColor = SIDE_COLOR[t.side] || "#555";
                const sideBg    = SIDE_BG[t.side] || "#f0f0f0";
                const qtyChange = Number(t.qtyChange);
                const qtyChangeStr = isNaN(qtyChange) ? "—" : (qtyChange > 0 ? `+${fmt(qtyChange, 0)}` : fmt(qtyChange, 0));
                return (
                  <tr key={t._row} style={{ borderBottom: "1px solid #f8f8f8" }}>
                    <td style={{ padding: "10px 12px", color: "#555", whiteSpace: "nowrap" }}>{fmtTs(t.timestamp)}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: sideBg, color: sideColor, fontWeight: 700, fontSize: 11 }}>
                        {t.side || t.action || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1a1a2e" }}>
                      {t.ticker}
                      {t.name && <div style={{ fontSize: 10, color: "#aaa", fontWeight: 400 }}>{t.name}</div>}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: sideColor, fontWeight: 600 }}>{qtyChangeStr}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#555" }}>{fmt(t.qtyAfter, 0)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{fmt(t.price)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#888" }}>{t.currency || "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#888" }}>{t.exchangeRate ? fmt(t.exchangeRate, 2) : "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#666", fontSize: 12 }}>{t.notes || ""}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={() => startEdit(t)} disabled={busy || editing} title="수정" style={{
                        padding: "4px 8px", fontSize: 11, border: "1px solid #e8e8e8", borderRadius: 6,
                        background: "#fff", cursor: editing ? "not-allowed" : "pointer", color: "#555", marginRight: 4,
                        opacity: editing ? 0.4 : 1,
                      }}>✏️</button>
                      <button onClick={() => remove(t)} disabled={busy || editing} title="삭제" style={{
                        padding: "4px 8px", fontSize: 11, border: "1px solid #fdd", borderRadius: 6,
                        background: "#fff5f5", cursor: editing ? "not-allowed" : "pointer", color: "#c0392b",
                        opacity: editing ? 0.4 : 1,
                      }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EditForm({ form, setForm, onSave, onCancel, busy }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputStyle = { padding: "6px 8px", fontSize: 12, border: "1px solid #ddd", borderRadius: 4, width: "100%", boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, color: "#888", marginBottom: 2, display: "block" };
  const isNew = !form._row;
  return (
    <div style={{ padding: "16px 20px", background: "#fffbf0", borderBottom: "1px solid #f0e0a0" }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: "#7c6200" }}>
        {isNew ? "새 매매내역 추가" : `매매내역 수정 (row ${form._row})`}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
        <div>
          <label style={labelStyle}>시각</label>
          <input type="datetime-local" value={form.timestamp} onChange={e => set("timestamp", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>구분 (side)</label>
          <select value={form.side} onChange={e => set("side", e.target.value)} style={inputStyle}>
            <option value="매수">매수</option>
            <option value="매도">매도</option>
            <option value="수정">수정</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>티커 *</label>
          <input value={form.ticker} onChange={e => set("ticker", e.target.value)} style={inputStyle} placeholder="NVDA / 5803 / 4062" />
        </div>
        <div>
          <label style={labelStyle}>이름</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle} placeholder="이비덴 등" />
        </div>
        <div>
          <label style={labelStyle}>마켓</label>
          <select value={form.market} onChange={e => set("market", e.target.value)} style={inputStyle}>
            {["US", "JP", "HK", "TW", "KR", "CN", "DE", "L"].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>수량 변화</label>
          <input type="number" value={form.qtyChange} onChange={e => set("qtyChange", e.target.value)} style={inputStyle} placeholder="매도면 -5000" />
        </div>
        <div>
          <label style={labelStyle}>수량(잔고)</label>
          <input type="number" value={form.qtyAfter} onChange={e => set("qtyAfter", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>단가</label>
          <input type="number" step="any" value={form.price} onChange={e => set("price", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>통화</label>
          <select value={form.currency} onChange={e => set("currency", e.target.value)} style={inputStyle}>
            {["USD", "JPY", "HKD", "TWD", "KRW", "CNY", "EUR", "GBP"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>환율 (→KRW)</label>
          <input type="number" step="any" value={form.exchangeRate} onChange={e => set("exchangeRate", e.target.value)} style={inputStyle} />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={labelStyle}>메모</label>
          <input value={form.notes} onChange={e => set("notes", e.target.value)} style={inputStyle} />
        </div>
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button onClick={onSave} disabled={busy} style={{
          padding: "8px 18px", fontSize: 13, fontWeight: 700, border: "none", borderRadius: 6,
          background: "#1a1a2e", color: "#fff", cursor: busy ? "wait" : "pointer",
        }}>{busy ? "저장 중..." : "저장"}</button>
        <button onClick={onCancel} disabled={busy} style={{
          padding: "8px 18px", fontSize: 13, border: "1px solid #ddd", borderRadius: 6,
          background: "#fff", cursor: "pointer", color: "#555",
        }}>취소</button>
      </div>
    </div>
  );
}
